import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { interceptConsoleLogsForPostHog } from './utils/posthogConsoleInterceptor';
import { initPush } from './native/push';

// Attach the push listeners at module scope, before React mounts. A notification tap
// that cold-starts the app fires its event before any component exists, so initPush
// buffers the deep link for the router to drain once it is ready. Registering later
// would drop that first tap. No-ops on web.
void initPush();

const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost = process.env.REACT_APP_POSTHOG_HOST;

// Note: OpenTelemetry frontend logging is disabled due to webpack module resolution issues
// Frontend logs are captured via PostHog events (see posthogConsoleInterceptor.ts)
// Backend logging via OpenTelemetry is working correctly

const options = {
  api_host: posthogHost,
  // Set ui_host to PostHog's actual domain for features like toolbar to work correctly
  // This is required when using a reverse proxy for api_host
  ui_host: 'https://us.posthog.com',
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: true, // Capture console.error calls for visibility into handled errors
  },
  // Enable console log recording - this captures console.log, console.info, console.warn, etc.
  // Note: Individual console logs are truncated at 2000 characters
  enable_recording_console_log: true,
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

if (posthogKey) {
  root.render(
    <React.StrictMode>
      <PostHogProvider 
        apiKey={posthogKey} 
        options={options}
      >
        <App />
        <PostHogConsoleInterceptor />
      </PostHogProvider>
    </React.StrictMode>
  );
} else {
  console.warn('PostHog API key not found. PostHog will not be initialized.');
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

/**
 * Component to initialize console log interception after PostHog is ready
 */
function PostHogConsoleInterceptor() {
  const posthog = usePostHog();

  React.useEffect(() => {
    if (posthog) {
      // PostHog is ready, set up console interception
      interceptConsoleLogsForPostHog();
    }
  }, [posthog]);

  return null;
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
